import React, { useState, useEffect, useRef, ChangeEvent, useImperativeHandle } from 'react';
import {
    Box,
    TextField,
    Typography,
    CircularProgress,
    Alert,
    Stack,
    InputAdornment,
    IconButton,
    Button
} from '@mui/material';

import { useStartSmsAuthMutation, useVerifySmsCodeMutation, VerifyResult } from '../../features/oidcSlice';
import { USCountryType } from '../../app/components/CountryCodes';
import { printError } from '../../app/utils';
import { PhoneMaskInput } from '../../app/auth/PhoneMaskInput';
import ClearIcon from "@mui/icons-material/Clear";

import { GetCountryTypeByLabelDefualtToUs } from '../../app/components/CountryCodes';
import { useAuthProfile } from '../../app/hooks';
import { CUSTOM_METHOD } from '../../app/auth/utils';
import { SmsConsent } from './SmsConsent';
import { formatPhone } from '../../app/auth/phoneEntryUtils';

export interface PhoneVerificationData {
    phone: string;
    verificationComplete: boolean;
    consentChecked: boolean | undefined;
}

export interface SmsPhoneValidationProps {
    value?: PhoneVerificationData;
    onChange: (data: PhoneVerificationData) => void;
    onBlur?: () => void;
    error?: boolean;
    helperText?: string;
    disabled?: boolean;
    required?: boolean;
    label?: string;
    showVerificationButtons?: boolean;
    //triggerVerification?: boolean;
    nextPressed?: number | undefined;
    //onNextPressedReset?: () => void;
    validatePhone?: (data: PhoneVerificationData) => { isValid: boolean; errorMessage: string };
    /** Don't render the internal SmsConsent block. Caller renders its own. */
    suppressConsent?: boolean;
    /** Don't render the "Phone number will not be saved without consent" warning. */
    suppressNoConsentWarning?: boolean;
    /** Extra gate on the SEND button, on top of phone validity + SMS consent. Used by
     *  callers that render their own additional required acceptances (see smsLogin). */
    sendDisabled?: boolean;
    /** Drop the "SMS consent must be checked to send" gate, and with it the
     *  "(Optional)" phone-label prefix. For callers where the messaging opt-in is
     *  optional and the phone is required — the code being sent is the login
     *  credential, gated by the caller's own `sendDisabled` (see smsLogin). */
    suppressConsentGate?: boolean;
    /** Fallback helper text under the phone field when there is no error and no
     *  caller-supplied `helperText`. */
    defaultHelperText?: string;
    /** Called with the raw verify-mutation result on successful verification. */
    onVerifySuccess?: (verifyResult: VerifyResult) => void;
    /** Fires `true` while an SMS send or verify is in flight, `false` otherwise.
     *  Parent can use this to hide/disable wizard Next button during the round-trip. */
    onBusyChange?: (busy: boolean) => void;
    /** Wizard-only: while we're in the `code` sub-step, register a back handler
     *  so the wizard's Back button returns to the phone sub-step instead of
     *  navigating to the previous wizard screen. Pass `null` to clear. */
    setStepBackHandler?: (handler: (() => void) | null) => void;
}

export const SmsPhoneValidation: React.FC<SmsPhoneValidationProps> = ({
    value,
    onChange,
    onBlur,
    error,
    helperText,
    disabled,
    required,
    label = "Phone Number",
    showVerificationButtons = true,
    // triggerVerification = false,
    nextPressed = undefined,
    //onNextPressedReset,
    validatePhone: externalValidatePhone,
    suppressConsent = false,
    suppressNoConsentWarning = false,
    sendDisabled = false,
    suppressConsentGate = false,
    defaultHelperText = "US phone numbers only",
    onVerifySuccess,
    onBusyChange,
    setStepBackHandler,
}) => {
    const { authMethod } = useAuthProfile()

    const [step, setStep] = useState<'phone' | 'code'>('phone');
    const [phone, setPhone] = useState(value?.phone ? formatPhone(value.phone.replace(/^\+1/, ''), false) : '');
    const [code, setCode] = useState('');
    const [Session, setSession] = useState('');
    const [userErrorMessage, setUserErrorMessage] = useState("");
    const [isPhoneValid, setIsPhoneValid] = useState<boolean | undefined>(undefined);
    const [consentChecked, setConsentChecked] = useState<boolean | undefined>(value?.consentChecked);
    const [hasBeenBlurred, setHasBeenBlurred] = useState(false);
    const [verificationComplete, setVerificationComplete] = useState(value?.verificationComplete || false);
    //const [showConsent, setShowConsent] = useState(!!phone);

    const inputRef = useRef<HTMLInputElement>(null);
    const smsConsentRef = useRef<{ getCurrentConsent(): boolean | undefined }>(null);
    const lastValRef = useRef<string>('');
    const isFirstEntryRef = useRef(true);

    // RTK Query hooks
    const [startAuth, { data: startData, isLoading: starting, isSuccess: startSuccess, error: startError }] = useStartSmsAuthMutation();
    const [verifyAuth, { data: verifyResult, isLoading: verifying, isSuccess: verifySuccess, error: verifyError, reset: verifyReset }] = useVerifySmsCodeMutation();

    const defaultedCountryValue = USCountryType;
    const errorMessage = (startError ? printError(startError) : '') || (verifyError ? printError(verifyError) : '') || null;

    // Initialize phone from prop value
    useEffect(() => {
        if (value?.phone && !phone) {
            setPhone(formatPhone(value.phone.replace(/^\+1/, ''), false));
        }
        if (value?.verificationComplete !== undefined) {
            setVerificationComplete(value.verificationComplete);
        }
        if (value?.consentChecked !== undefined) {
            setConsentChecked(value.consentChecked);
        }
    }, [value]);

    // // Monitor phone number changes to show/hide consent
    // useEffect(() => {
    //     setShowConsent(!!phone);
    // }, [phone]);

    // Step transition when session is received
    useEffect(() => {
        if (startSuccess && startData?.Session) {
            setSession(startData.Session);
            setStep('code');
        }
    }, [startSuccess, startData]);

    // Publish busy state to parent — used to hide the wizard's Next button
    // during SMS send or verify round-trip so the user can't double-submit.
    useEffect(() => {
        onBusyChange?.(starting || verifying);
    }, [starting, verifying, onBusyChange]);

    // Safety reset on unmount: ensure the parent's busy flag is cleared even
    // if the component unmounts mid-flight (e.g., wizard advances to the next
    // step while verifying was true, or any other unmount timing race).
    useEffect(() => {
        return () => {
            onBusyChange?.(false);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Register a back handler with the wizard while we're in the `code`
    // sub-step. When the user presses the wizard's Back button, it returns to
    // the phone sub-step instead of navigating to the previous wizard screen.
    // Note: useState setters treat a function argument as an updater, so to
    // set state to a function value we pass () => fn (the wrapper returns fn).
    useEffect(() => {
        if (!setStepBackHandler) return;
        if (step === 'code') {
            const handler = () => handleBackToPhone();
            (setStepBackHandler as any)(() => handler);
        } else {
            setStepBackHandler(null);
        }
        return () => setStepBackHandler(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [step]);

    // // Handle external verification trigger
    // useEffect(() => {
    //     if (triggerVerification && step === 'code' && code) {
    //         handleVerifyCode();
    //     }
    // }, [triggerVerification, step, code]);

    // Handle verification success
    useEffect(() => {
        if (verifySuccess && verifyResult) {
            if (verifyResult.success) {
                // Verification successful, update the form value
                const formattedPhone = formatPhone(phone.replace(/^\+1/, ''), false);
                onChange({ phone: formattedPhone, verificationComplete: true, consentChecked: consentChecked || false });
                if (onVerifySuccess) onVerifySuccess(verifyResult);
                setUserErrorMessage("");
                setStep('phone'); // Reset to phone step for future edits
                setCode('');
                setConsentChecked(false);
                setVerificationComplete(true);
            } else {
                setSession(verifyResult.session!);
                setUserErrorMessage("Code did not match, please try again");
                setVerificationComplete(false);
            }
        }
        if (verifyError) {
            setUserErrorMessage('Verification failed');
            setVerificationComplete(false);
        }
        if (startError) {
            setUserErrorMessage('Sorry, we are unable to send verification code at this time, please try again later');
        }
    }, [verifySuccess, verifyError, startError, phone, onChange]);

    // Handle nextPressed trigger from wizard
    useEffect(() => {
        if (nextPressed) {
            // Skip handleSendCode on first entry to prevent stale state triggers
            if (!isFirstEntryRef.current) {
                // Component decides what to do based on current state
                if (step === 'phone' && !verificationComplete) {
                    // Check consent before sending code
                    const result = validatePhoneSetState();
                    if (result.phoneData?.consentChecked) {
                        handleSendCode();
                    } else {
                        // No consent - don't send code, allow wizard to advance
                        console.log('No consent provided - skipping SMS verification');
                    }
                } else if (step === 'code' && code) {
                    handleVerifyCode();
                }
            }
            isFirstEntryRef.current = false;
            // Reset the flag after handling
            //onNextPressedReset?.();
        }
    }, [nextPressed]);  //, step, phone, code, verificationComplete, onNextPressedReset]);

    const defaultValidatePhone = (data: PhoneVerificationData) => {
        const phoneDigits = data.phone.replace(/[()\-\s]/g, "");
        if (phoneDigits.length !== 10) {            
            return {isValid: false, errorMessage: "10 digit phone number required"};
        }
        return {isValid: true, errorMessage: ""};
    };

    const validatePhoneSetState = () => {
        // Get current values directly from refs and DOM
        const currentPhone = inputRef.current?.value || '';
        const currentConsentChecked = smsConsentRef.current?.getCurrentConsent() ?? undefined;
        
        const currentPhoneData = { 
            phone: currentPhone, 
            verificationComplete: verificationComplete, 
            consentChecked: currentConsentChecked 
        };
        
        const validator = externalValidatePhone || defaultValidatePhone;
        const { isValid, errorMessage } = validator(currentPhoneData);
        
        if (isValid) {
            setIsPhoneValid(true);
            setUserErrorMessage("");
            return { isValid: true, phoneData: currentPhoneData };
        }
        else {
            setUserErrorMessage(errorMessage);
            setIsPhoneValid(false);
            return { isValid: false, phoneData: null };
        }
    };

    const handleSendCode = () => {
        const result = validatePhoneSetState();
        if (result.isValid) {
            startAuth({ phone: `+1${phone.replace(/[()\-\s]/g, "")}` });
        }
    };

    const handleVerifyCode = async () => {
        if (!code || !Session) return;
        setUserErrorMessage("");
        try {
            const result = await verifyAuth({ code, Session }).unwrap();
            if (result.success) {
                // Verification successful, update the form value
                const formattedPhone = formatPhone(phone.replace(/^\+1/, ''), false);
                onChange({ phone: formattedPhone, verificationComplete: true, consentChecked: consentChecked || false });
                if (onVerifySuccess) onVerifySuccess(result);
                setUserErrorMessage("");
                //setStep('phone'); // Reset to phone step for future edits
                setCode('');
                //setConsentChecked(false);
                setVerificationComplete(true);
            } else {
                setSession(result.session!);
                setUserErrorMessage("Code did not match, please try again");
                setVerificationComplete(false);
            }
        } catch (error) {
            setUserErrorMessage('Verification failed');
            setVerificationComplete(false);
        }
    };

    const handlePhoneChange = (newPhone: string) => {
        // react-imask fires onAccept on mount with the normalized value, which
        // would otherwise wipe the verified form state. Treat a no-op emission
        // as no edit.
        if (newPhone === phone) return;

        setPhone(newPhone);
        setUserErrorMessage("");
        setVerificationComplete(false);
        // Propagate the edit to the form. Carry the new phone so the validator
        // can run against it; `verificationComplete: false` flags that re-verify
        // is required before this can be saved.
        if (step === 'phone') {
            onChange({ phone: newPhone, verificationComplete: false, consentChecked: consentChecked || false });
        }
    };

    const handleBackToPhone = () => {
        setStep('phone');
        setCode('');
        setUserErrorMessage("");
        if (verifyError) {
            verifyReset();
        }
    };

    const handleClearPhone = () => {
        setPhone("");
        onChange({ phone: "", verificationComplete: false, consentChecked: consentChecked || false });
        setUserErrorMessage("");
    };

    // Expose verification status for form validation
    const isVerificationComplete = () => {
        return verificationComplete;
    };

    return (
        <Box>
            <Stack sx={{ width: '100%', gap: 1 }}>
                {/* {(userErrorMessage && !helperText) && (
                    <Alert severity="error" sx={{ mb: 1 }}>
                        {userErrorMessage}
                    </Alert>
                )} */}

                {/* Warning when phone exists but no consent — gated by
                    suppressNoConsentWarning so callers like SmsLogin (which renders
                    its own consent UX) can hide it without affecting other behavior. */}
                {!suppressNoConsentWarning && (phone.trim() !== '' && consentChecked === false) && (
                    <Alert 
                        severity="warning" 
                        sx={{ 
                            mb: 1,
                            borderRadius: 2
                        }}
                    >
                        Phone number will not be saved without consent to receive messages
                    </Alert>
                )}

                {step === 'phone' && (
                    <>
                        <Stack direction={'row'} spacing={1} justifyContent="flex-start" alignItems="center" sx={{ mt: 0, width: '100%' }}>
                            <img
                                src={`https://flagcdn.com/${defaultedCountryValue.code.toLowerCase()}.svg`}
                                width="45"
                                alt={defaultedCountryValue.label}
                            />

                            <TextField
                                fullWidth
                                autoFocus
                                disabled={disabled}
                                label={`${authMethod === CUSTOM_METHOD ? "This" : (suppressConsentGate || consentChecked === true ? "" : "(Optional)")} Phone Number${authMethod === CUSTOM_METHOD ? " was used to log in and can't be changed" : ""}`}
                                placeholder="1234567890"
                                variant="outlined"
                                margin="dense"
                                inputRef={inputRef}
                                value={phone.replace(/^\+1/, '') ?? ''}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                    handlePhoneChange(e.target.value);
                                }}
                                onFocus={() => { setHasBeenBlurred(false); }}
                                onBlur={() => {
                                    setHasBeenBlurred(true);
                                    if (!verificationComplete) {
                                        const result = validatePhoneSetState();
                                        if (result.isValid && result.phoneData) {
                                            onChange(result.phoneData);
                                        }
                                        // If invalid, don't call onChange - validation function handles error state
                                    }

                                    if (onBlur) onBlur();
                                }}
                                error={error || !!userErrorMessage}
                                helperText={helperText || userErrorMessage || defaultHelperText}
                                InputLabelProps={{
                                    shrink: true
                                }}
                                InputProps={{
                                    inputComponent: PhoneMaskInput as any,
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Typography variant="body2" sx={{ color: '#888', fontWeight: 500 }}>+1</Typography>
                                        </InputAdornment>
                                    ),
                                    endAdornment: (phone.replace(/^\+1/, '')) ? (
                                        <InputAdornment position="end">
                                            <IconButton
                                                size="small"
                                                onClick={() => {
                                                    handleClearPhone();
                                                    setConsentChecked(false);
                                                    //onChange({ phone: '', isPhoneValid: undefined, verificationComplete: false, consentChecked: false });
                                                }}
                                                disabled={disabled}
                                            >
                                                <ClearIcon fontSize="small" />
                                            </IconButton>
                                        </InputAdornment>
                                    ) : null
                                }}
                            />
                        </Stack>

                        {!suppressConsent && (
                            <SmsConsent
                                consentChecked={consentChecked}
                                smsConsentRef={smsConsentRef}
                                onConsentChange={(checked) => {
                                    setConsentChecked(checked);

                                    // Always propagate the consent change up — even
                                    // when the resulting state fails validation. The
                                    // wizard's react-hook-form validate rule needs to
                                    // see the current consent value to correctly block
                                    // Next on consent-checked-but-empty-phone. We still
                                    // run validatePhoneSetState locally to surface the
                                    // inline error inside this component.
                                    const result = validatePhoneSetState();
                                    onChange(result.phoneData ?? {
                                        phone: inputRef.current?.value || '',
                                        verificationComplete: verificationComplete,
                                        consentChecked: checked,
                                    });
                                }}
                            />
                        )}
                        {showVerificationButtons ? (
                            <Button
                                fullWidth
                                variant="contained"
                                onClick={handleSendCode}
                                disabled={phone.length <= 2 || starting || (!suppressConsentGate && !consentChecked) || disabled || sendDisabled}
                                sx={{ mt: 1 }}
                            >
                                {starting ? <CircularProgress size={24} /> : 'SEND VERIFICATION CODE'}
                            </Button>
                        ) : ""}
                    </>
                )}

                {(step === 'code' && verifyError) && (
                    <>
                        {showVerificationButtons ? <Button
                            fullWidth
                            variant="contained"
                            onClick={handleBackToPhone}
                            disabled={starting || disabled}
                            sx={{ mt: 2 }}
                        >
                            {starting ? <CircularProgress size={24} /> : 'Send Another Code'}
                        </Button> : ""}
                    </>)}

                {(step === 'code' && !verifyError) && (
                    <>
                        <Typography variant="body1" mb={1}>
                            ✅ Code Sent
                        </Typography>

                        <Typography variant="body2" mb={2}>
                            We've sent a 6-digit code to <strong>{phone}</strong>. It may take a few seconds to arrive.
                            Please check your text messages and enter the code below.
                        </Typography>

                        <TextField
                            fullWidth
                            autoFocus
                            label="Verification code"
                            value={code}
                            onChange={(e) => setCode(e.target.value)}
                            margin="normal"
                            disabled={disabled}
                        />
                        {showVerificationButtons ?
                            <Button
                                fullWidth
                                variant="contained"
                                onClick={handleVerifyCode}
                                disabled={!code || verifying || disabled}
                                sx={{ mt: 2 }}
                            >
                                {verifying ? <CircularProgress size={24} /> : 'Verify Code'}
                            </Button>
                            : ""}
                        {!verifying && <Button
                            fullWidth
                            variant="text"
                            sx={{ mt: 1, color: '#656EFF' }}
                            onClick={handleBackToPhone}
                            disabled={disabled}
                        >
                            Change phone number
                        </Button>}

                    </>
                )}

                {(verifying || starting) && (
                    <Typography variant="body2" align="center" sx={{ color: '#656EFF', fontWeight: 500 }}>
                        {verifying ? "Verifying your code, please wait…" : "Sending verification code, please wait…"}
                    </Typography>
                )}
            </Stack>
        </Box>
    );
};

// export interface SmsConsentProps {
//     consentChecked: boolean | undefined;
//     onConsentChange: (checked: boolean) => void;
//     smsConsentRef?: React.Ref<{ getCurrentConsent(): boolean | undefined }>;
// }

// export const SmsConsent: React.FC<SmsConsentProps> = ({
//     consentChecked,
//     onConsentChange,
//     smsConsentRef
// }) => {
//     const consentCheckboxRef = useRef<HTMLInputElement>(null);
//     const nonConsentCheckboxRef = useRef<HTMLInputElement>(null);

//     // Expose method to get current consent state
//     useImperativeHandle(smsConsentRef, () => ({
//         getCurrentConsent: (): boolean | undefined => {
//             if (consentCheckboxRef.current?.checked) return true;
//             if (nonConsentCheckboxRef.current?.checked) return false;
//             return undefined;
//         }
//     }), []);

//     const handleConsentChange = (
//         event: ChangeEvent<HTMLInputElement>
//     ): void => {
//         const isChecked = event.target.checked;
        
//         // Immediate DOM-based mutual exclusivity
//         if (isChecked && nonConsentCheckboxRef.current) {
//             nonConsentCheckboxRef.current.checked = false;
//         }
        
//         // Trigger parent change with correct state
//         onConsentChange(isChecked);
//     };

//     const handleNonConsentChange = (
//         event: ChangeEvent<HTMLInputElement>
//     ): void => {
//         const isChecked = event.target.checked;
        
//         // Immediate DOM-based mutual exclusivity
//         if (isChecked && consentCheckboxRef.current) {
//             consentCheckboxRef.current.checked = false;
//         }
        
//         // Trigger parent change with correct state (negated for non-consent)
//         onConsentChange(!isChecked);
//     };

//     return (
//         <>
//             <Box sx={{
//                 borderRadius: 2,
//                 backgroundColor: 'rgba(101, 110, 255, 0.04)',
//                 border: '1px solid rgba(164, 142, 233, 0.12)',
//                 p: 1.5,
//                 mb: 1.5,
//             }}>
//                 <Stack spacing={2}>
//                     <Typography component="label" sx={{ display: "flex", alignItems: "flex-start", cursor: "pointer" }}>
//                         <input
//                             ref={consentCheckboxRef}
//                             type="checkbox"
//                             checked={consentChecked === true}
//                             onChange={handleConsentChange}
//                             style={{ marginRight: '8px', marginTop: '2px' }}
//                         />
//                         <Box>
//                             {smsDisclosure("By checking this box you agree ")}
//                         </Box>
//                     </Typography>
//                     <Typography component="label" sx={{ display: "flex", alignItems: "flex-start", cursor: "pointer" }}>
//                         <input
//                             ref={nonConsentCheckboxRef}
//                             type="checkbox"
//                             checked={consentChecked === false}
//                             onChange={handleNonConsentChange}
//                             style={{ marginRight: '8px', marginTop: '2px' }}
//                         />
//                         <Box>
//                             {smsDisclosureOption("I do not wish to receive messages from Polygon Health")}
//                         </Box>
//                     </Typography>
//                 </Stack>
//             </Box>
//         </>
//     );
// };
